import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { issuesApi } from '../utils/api';
import { formatDateTimeInTimezone } from '../utils/dateUtils';
import { AlertTriangleIcon, CircleCheckIcon, PhoneIcon, CameraIcon } from './icons/LucideIcons';
import { buttonClass, inputClass } from './settings/SettingsUI';
import TextReactionLine from './TextReactionLine';
import { describeTextReaction } from '../utils/textReactions';
import PageHeader from './PageHeader';
import { forestLinkClass, stampClass } from './orderDetailsUI';

// Problem reports from the delivered tracking page. Used on the Issues page
// (every order) and inside an order's details (one order). Staff call the
// customer, then resolve with a one-line note. Resolving changes nothing
// else: review asks go out on their own schedule either way.

export const ISSUE_LABELS = {
  replacement: 'Arrived damaged or wilted',
  wrong_item: 'Not what they ordered',
  missing: "Can't find it",
  misdelivery: 'Went to the wrong address',
  late: 'Arrived late',
  card: 'Card message wrong or missing',
  other: 'Something else',
  text_reply: 'Text reply',
  // Driver returned the stop to the shop (the order is back on Dispatch, unrouted)
  no_one_available: 'Returned: no one available',
  bad_address: 'Returned: wrong or bad address',
  refused: 'Returned: refused',
};

// Resolve is a small secondary on the row; it opens the one-line note form,
// which is still required before the report is resolved.
function ResolveForm({ issue, onResolved, onNotification, onCancel }) {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    setSaving(true);
    try {
      await issuesApi.resolve(issue.id, note.trim());
      onNotification?.('Problem report resolved', 'success');
      onResolved();
    } catch (error) {
      onNotification?.(error.message || 'Could not resolve the report', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
      <input
        type="text"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="How was it resolved? e.g. Called, sending a replacement at 2"
        aria-label="How it was resolved"
        maxLength={1000}
        className="input-field min-w-0 flex-1"
        autoFocus
      />
      <div className="flex shrink-0 gap-2">
        <button type="submit" disabled={saving || !note.trim()} className="btn-primary">
          {saving ? 'Saving…' : 'Save and resolve'}
        </button>
        <button type="button" onClick={onCancel} className="btn-ghost">Cancel</button>
      </div>
    </form>
  );
}

// Open = warn tag, resolved = ok tag (StatusTag's Forest pill geometry; these
// are not order statuses, so they don't go through StatusTag's map).
const OPEN_TAG = `${stampClass} tag-dot bg-warn-bg text-warn-fg`;
const RESOLVED_TAG = `${stampClass} tag-check bg-ok-bg text-ok-fg`;

function IssueItem({ issue, showOrder, onResolved, onNotification }) {
  const [resolving, setResolving] = useState(false);
  const open = issue.status === 'open';
  const orderNumber = issue.shopify_order_name || issue.order_number;
  return (
    <li className={`min-h-row border-b border-gray-200 px-4 py-3 last:border-b-0 sm:px-7 ${open ? '' : 'settled-recede'}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <span className={open ? OPEN_TAG : RESOLVED_TAG}>{open ? 'Open' : 'Resolved'}</span>
        <span className="text-sm font-semibold text-gray-900">{ISSUE_LABELS[issue.category] || issue.category}</span>
        <span className="text-sm text-gray-600">from the {issue.audience}</span>
        {showOrder && (
          <Link to={`/orders/${encodeURIComponent(issue.order_number)}`} className={`text-sm ${forestLinkClass}`}>
            Order {orderNumber}
          </Link>
        )}
        <span className="tabular ml-auto text-xs text-gray-500">{formatDateTimeInTimezone(issue.created_at)}</span>
        {open && !resolving && (
          <button type="button" onClick={() => setResolving(true)} className="btn-secondary btn-sm">Resolve</button>
        )}
      </div>

      {(issue.contact_name || issue.contact_phone || issue.photo_url) && (
        <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-700">
          {issue.contact_name && <span>{issue.contact_name}</span>}
          {issue.contact_phone && (
            <a href={`tel:${issue.contact_phone}`} className={`inline-flex items-center gap-1 ${forestLinkClass}`}>
              <PhoneIcon size={14} /> {issue.contact_phone}
            </a>
          )}
          {issue.photo_url && (
            <a href={issue.photo_url} target="_blank" rel="noreferrer" className={`inline-flex items-center gap-1 ${forestLinkClass}`}>
              <CameraIcon size={14} /> Photo
            </a>
          )}
        </div>
      )}

      {issue.body && (describeTextReaction(issue.body)
        ? <TextReactionLine reaction={describeTextReaction(issue.body)} className="mt-1.5 text-sm text-gray-900" />
        : <p className="mt-1.5 whitespace-pre-line text-sm text-gray-900">{issue.body}</p>)}
      {open && issue.category === 'text_reply' && (
        <Link to={`/orders/${encodeURIComponent(issue.order_number)}?tab=messages`} className={`mt-1.5 inline-block text-sm ${forestLinkClass}`}>
          Reply by text
        </Link>
      )}

      {open && resolving && (
        <ResolveForm issue={issue} onResolved={onResolved} onNotification={onNotification} onCancel={() => setResolving(false)} />
      )}
      {!open && (
        <p className="mt-1.5 text-sm text-gray-600">
          Resolved by {issue.resolved_by || 'staff'} {issue.resolved_at ? formatDateTimeInTimezone(issue.resolved_at) : ''}
          {issue.resolution_note ? `: ${issue.resolution_note}` : ''}
        </p>
      )}
    </li>
  );
}

// One open report as a banner at the top of an order's details (Bouqmark Order
// Detail design). Resolve still asks for the one-line note before it saves.
function OrderIssueBanner({ issue, onReply, onResolved, onNotification }) {
  const [resolving, setResolving] = useState(false);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const isTextReply = issue.category === 'text_reply';

  const resolve = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    setSaving(true);
    try {
      await issuesApi.resolve(issue.id, note.trim());
      onNotification?.('Problem report resolved', 'success');
      onResolved();
    } catch (error) {
      onNotification?.(error.message || 'Could not resolve the report', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div role="alert" className="flex flex-wrap items-start gap-3.5 rounded-[20px] border border-floral-300 bg-floral-100 px-5 py-4">
      <span className="grid h-8 w-8 flex-shrink-0 place-items-center rounded-full bg-floral-500 text-floral-50">
        <AlertTriangleIcon size={16} />
      </span>
      <div className="flex min-w-0 flex-[1_1_240px] flex-col gap-1.5">
        <div className="flex flex-wrap items-baseline gap-2.5">
          <span className="text-[15px] font-semibold text-floral-900">
            {isTextReply ? `The ${issue.audience} texted back` : `The ${issue.audience} reported a problem`}
          </span>
          <span className="text-xs text-floral-800">{formatDateTimeInTimezone(issue.created_at)}</span>
        </div>
        {!isTextReply && (
          <span className="inline-flex self-start rounded-full border border-floral-500 bg-floral-300 px-2.5 py-0.5 text-[11px] font-semibold text-floral-900">
            {ISSUE_LABELS[issue.category] || issue.category}
          </span>
        )}
        {issue.body && (describeTextReaction(issue.body)
          ? <TextReactionLine reaction={describeTextReaction(issue.body)} className="text-[13px] text-floral-900" />
          : <span className="whitespace-pre-line text-[13px] text-floral-900">“{issue.body}”</span>)}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px]">
          {issue.contact_name && <span className="text-floral-900">{issue.contact_name}</span>}
          {issue.contact_phone && (
            <a href={`tel:${issue.contact_phone}`} className="inline-flex items-center gap-1 font-semibold text-floral-700">
              <PhoneIcon size={13} /> {issue.contact_phone}
            </a>
          )}
          {issue.photo_url && (
            <a href={issue.photo_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-semibold text-floral-700">
              <CameraIcon size={13} /> Photo
            </a>
          )}
        </div>
        {resolving && (
          <form onSubmit={resolve} className="mt-1 flex flex-col gap-2 sm:flex-row">
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="How was it resolved? e.g. Called, sending a replacement at 2"
              aria-label="How it was resolved"
              maxLength={1000}
              className={`${inputClass} min-w-0 flex-1`}
              autoFocus
            />
            <button type="submit" disabled={saving || !note.trim()} className={`${buttonClass.primary} text-[13px]`}>
              {saving ? 'Saving…' : 'Save and resolve'}
            </button>
          </form>
        )}
      </div>
      {!resolving && (
        <div className="flex flex-shrink-0 gap-2">
          {onReply && <button type="button" onClick={onReply} className={`${buttonClass.ghost} text-[13px]`}>Reply</button>}
          <button type="button" onClick={() => setResolving(true)} className={`${buttonClass.primary} text-[13px]`}>Resolve</button>
        </div>
      )}
    </div>
  );
}

/**
 * @param {string} [orderNumber]  limit to one order (shows open and resolved)
 * @param {Function} [onReply]    order view only: open the order's messages
 */
const IssuesList = ({ orderNumber, onNotification, onReply }) => {
  const [status, setStatus] = useState(orderNumber ? 'all' : 'open');
  const [issues, setIssues] = useState(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    setFailed(false);
    try {
      const res = await issuesApi.list({ status, orderId: orderNumber });
      setIssues(res?.data || []);
    } catch {
      setFailed(true);
    }
  }, [status, orderNumber]);

  useEffect(() => { load(); }, [load]);

  const handleResolved = () => {
    load();
    window.dispatchEvent(new Event('rosa:issues-changed'));
  };

  // On an order with no reports, show nothing at all.
  if (orderNumber && issues?.length === 0) return null;

  // On an order: open reports as banners, resolved ones as a quiet line each.
  if (orderNumber) {
    if (failed) return <p className="text-sm text-danger-fg">Could not load problem reports. Reopen the order to try again.</p>;
    if (!issues) return null;
    return (
      <div className="flex flex-col gap-2.5">
        {issues.filter((i) => i.status === 'open').map((issue) => (
          <OrderIssueBanner key={issue.id} issue={issue} onReply={onReply} onResolved={handleResolved} onNotification={onNotification} />
        ))}
        {issues.filter((i) => i.status !== 'open').map((issue) => (
          <p key={issue.id} className="flex items-center gap-2 px-1 text-[13px] text-gray-600">
            <CircleCheckIcon size={14} className="flex-shrink-0 text-accent2-700" />
            <span>
              {ISSUE_LABELS[issue.category] || issue.category} — resolved by {issue.resolved_by || 'staff'}
              {issue.resolved_at ? ` ${formatDateTimeInTimezone(issue.resolved_at)}` : ''}
              {issue.resolution_note ? `: ${issue.resolution_note}` : ''}
            </span>
          </p>
        ))}
      </div>
    );
  }

  // Issues page. App.js gives this route the shared page padding; the negative
  // margins undo the route padding so the header band runs edge to edge.
  return (
    <section className="-mx-2 sm:-mx-4 lg:-mx-6 -mt-6">
      <PageHeader
        title="Problem reports"
        actions={
          <div className="seg" role="group" aria-label="Show">
            {['open', 'resolved'].map((s) => (
              <button
                key={s}
                type="button"
                aria-pressed={status === s}
                onClick={() => setStatus(s)}
                className={`seg-item capitalize ${status === s ? 'seg-item-active' : ''}`}
              >
                {s}
              </button>
            ))}
          </div>
        }
      />
      {failed && <p className="px-4 py-4 text-sm text-danger-fg sm:px-7">Could not load problem reports. Refresh to try again.</p>}
      {!failed && issues === null && <p className="px-4 py-4 text-sm text-gray-600 sm:px-7">Loading…</p>}
      {!failed && issues?.length === 0 && (
        <div className="empty-state py-12">
          <div className="empty-state-icon"><CircleCheckIcon size={20} /></div>
          <span className="empty-state-text">
            {status === 'open' ? 'No open problem reports. Customers send these from the tracking page.' : 'Nothing resolved yet.'}
          </span>
        </div>
      )}
      {!failed && issues?.length > 0 && (
        <ul>
          {issues.map((issue) => (
            <IssueItem key={issue.id} issue={issue} showOrder onResolved={handleResolved} onNotification={onNotification} />
          ))}
        </ul>
      )}
    </section>
  );
};

export default IssuesList;
